/**
 * Centralized route-to-documentation mapping for Access Control Admin.
 *
 * This file is the single source of truth for in-app help links.
 * Every route should have a corresponding documentation entry.
 *
 * CI validates that all routes have entries here.
 * See: tools/scripts/validate-help-links.ts
 */

import type { DocsLinkConfig, HelpLinkEntry } from '@digiwedge/ui-docs-link';

/**
 * Route-to-documentation mapping.
 *
 * Routes are matched in order; first match wins.
 * Use '/*' suffix for wildcard matching (e.g., '/tenants/*').
 */
export const HELP_LINKS: HelpLinkEntry[] = [
  // ─────────────────────────────────────────────────────────────────
  // Dashboard & Overview
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/',
    docPath: '/getting-started',
    label: 'Access Control Overview',
  },
  {
    route: '/dashboard',
    docPath: '/getting-started',
    anchor: 'dashboard',
    label: 'Dashboard',
  },

  // ─────────────────────────────────────────────────────────────────
  // Authentication
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/login',
    docPath: '/authentication',
    label: 'Authentication',
  },
  {
    route: '/logout',
    docPath: '/authentication',
    label: 'Authentication',
  },
  {
    route: '/forgot-password',
    docPath: '/authentication',
    anchor: 'password-reset',
    label: 'Password Recovery',
  },
  {
    route: '/notifications',
    docPath: '/auth/notifications',
    label: 'Notifications',
  },
  {
    route: '/reset-password/:token',
    docPath: '/authentication',
    anchor: 'password-reset',
    label: 'Password Recovery',
  },
  {
    route: '/register',
    docPath: '/getting-started',
    anchor: 'register',
    label: 'Registration',
  },
  {
    route: '/health',
    docPath: '/getting-started',
    anchor: 'health',
    label: 'Health & Status',
  },

  // ─────────────────────────────────────────────────────────────────
  // Tenant Management
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/tenants',
    docPath: '/admin-guide',
    anchor: 'tenants',
    label: 'Tenant Management',
    adminOnly: true,
  },
  {
    route: '/tenants/create',
    docPath: '/admin-guide',
    anchor: 'create-a-tenant',
    label: 'Create Tenant',
    adminOnly: true,
  },
  {
    route: '/tenants/:tenantId/edit',
    docPath: '/admin-guide',
    anchor: 'edit-a-tenant',
    label: 'Edit Tenant',
    adminOnly: true,
  },
  {
    route: '/tenants/:tenantId/scim',
    docPath: '/admin-guide',
    anchor: 'scim-provisioning',
    label: 'SCIM Provisioning',
    adminOnly: true,
  },
  {
    route: '/tenants/:tenantId/saml',
    docPath: '/admin-guide',
    anchor: 'saml-sso',
    label: 'SAML SSO',
    adminOnly: true,
  },
  {
    route: '/tenants/:tenantId',
    docPath: '/admin-guide',
    anchor: 'tenants',
    label: 'Tenant Details',
    adminOnly: true,
  },

  // ─────────────────────────────────────────────────────────────────
  // Onboarding Wizards
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/onboarding/voicepro',
    docPath: '/onboarding-wizard',
    anchor: 'voicepro',
    label: 'VoicePro Onboarding',
    adminOnly: true,
  },

  // ─────────────────────────────────────────────────────────────────
  // User Management
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/user-profiles',
    docPath: '/user-profiles',
    label: 'User Profiles',
    adminOnly: true,
  },
  {
    route: '/users',
    docPath: '/user-management',
    anchor: 'users',
    label: 'User Management',
    adminOnly: true,
  },
  {
    route: '/users/create',
    docPath: '/user-management',
    anchor: 'create-a-user',
    label: 'Create User',
    adminOnly: true,
  },
  {
    route: '/users/:userId/edit',
    docPath: '/user-management',
    anchor: 'edit-a-user',
    label: 'Edit User',
    adminOnly: true,
  },
  {
    route: '/users/:userId',
    docPath: '/user-management',
    anchor: 'view-user-details',
    label: 'User Details',
    adminOnly: true,
  },

  // ─────────────────────────────────────────────────────────────────
  // Access Control Entities
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/permissions',
    docPath: '/admin-guide',
    anchor: 'permissions',
    label: 'Permissions',
    adminOnly: true,
  },
  {
    route: '/permissions/create',
    docPath: '/admin-guide',
    anchor: 'create-a-permission',
    label: 'Create Permission',
    adminOnly: true,
  },
  {
    route: '/permissions/:permissionId/edit',
    docPath: '/admin-guide',
    anchor: 'edit-a-permission',
    label: 'Edit Permission',
    adminOnly: true,
  },
  {
    route: '/permissions/:permissionId',
    docPath: '/admin-guide',
    anchor: 'view-permission-details',
    label: 'Permission Details',
    adminOnly: true,
  },
  {
    route: '/roles',
    docPath: '/admin-guide',
    anchor: 'roles',
    label: 'Roles',
    adminOnly: true,
  },
  {
    route: '/roles/create',
    docPath: '/admin-guide',
    anchor: 'create-a-custom-role',
    label: 'Create Role',
    adminOnly: true,
  },
  {
    route: '/roles/:roleId/edit',
    docPath: '/admin-guide',
    anchor: 'edit-a-role',
    label: 'Edit Role',
    adminOnly: true,
  },
  {
    route: '/roles/:roleId',
    docPath: '/admin-guide',
    anchor: 'view-role-details',
    label: 'Role Details',
    adminOnly: true,
  },
  {
    route: '/permission-assignments',
    docPath: '/admin-guide',
    anchor: 'permission-assignments',
    label: 'Permission Assignments',
    adminOnly: true,
  },
  {
    route: '/permission-assignments/create',
    docPath: '/admin-guide',
    anchor: 'create-a-permission-assignment',
    label: 'Create Permission Assignment',
    adminOnly: true,
  },
  {
    route: '/permission-assignments/:assignmentId/edit',
    docPath: '/admin-guide',
    anchor: 'edit-a-permission-assignment',
    label: 'Edit Permission Assignment',
    adminOnly: true,
  },
  {
    route: '/permission-assignments/:assignmentId',
    docPath: '/admin-guide',
    anchor: 'view-permission-assignment-details',
    label: 'Permission Assignment Details',
    adminOnly: true,
  },
  {
    route: '/features',
    docPath: '/admin-guide',
    anchor: 'features',
    label: 'Features',
    adminOnly: true,
  },
  {
    route: '/features/create',
    docPath: '/admin-guide',
    anchor: 'create-a-feature',
    label: 'Create Feature',
    adminOnly: true,
  },
  {
    route: '/features/:featureId/edit',
    docPath: '/admin-guide',
    anchor: 'edit-a-feature',
    label: 'Edit Feature',
    adminOnly: true,
  },
  {
    route: '/features/:featureId',
    docPath: '/admin-guide',
    anchor: 'view-feature-details',
    label: 'Feature Details',
    adminOnly: true,
  },
  {
    route: '/feature-flags',
    docPath: '/admin-guide',
    anchor: 'feature-flags',
    label: 'Feature Flags',
    adminOnly: true,
  },
  {
    route: '/feature-flags/create',
    docPath: '/admin-guide',
    anchor: 'create-a-feature-flag',
    label: 'Create Feature Flag',
    adminOnly: true,
  },
  {
    route: '/feature-flags/:flagId/edit',
    docPath: '/admin-guide',
    anchor: 'edit-a-feature-flag',
    label: 'Edit Feature Flag',
    adminOnly: true,
  },
  {
    route: '/feature-flags/:flagId',
    docPath: '/admin-guide',
    anchor: 'view-feature-flag-details',
    label: 'Feature Flag Details',
    adminOnly: true,
  },
  {
    route: '/consents',
    docPath: '/admin-guide',
    anchor: 'consents',
    label: 'Consents',
    adminOnly: true,
  },
  {
    route: '/consents/create',
    docPath: '/admin-guide',
    anchor: 'create-a-consent',
    label: 'Create Consent',
    adminOnly: true,
  },
  {
    route: '/consents/:consentId/edit',
    docPath: '/admin-guide',
    anchor: 'edit-a-consent',
    label: 'Edit Consent',
    adminOnly: true,
  },
  {
    route: '/consents/:consentId',
    docPath: '/admin-guide',
    anchor: 'view-consent-details',
    label: 'Consent Details',
    adminOnly: true,
  },
  {
    route: '/invitations',
    docPath: '/user-management',
    anchor: 'invitations',
    label: 'Invitations',
    adminOnly: true,
  },
  {
    route: '/invitations/create',
    docPath: '/user-management',
    anchor: 'invite-a-new-user',
    label: 'Create Invitation',
    adminOnly: true,
  },
  {
    route: '/invitations/:invitationId/edit',
    docPath: '/user-management',
    anchor: 'edit-an-invitation',
    label: 'Edit Invitation',
    adminOnly: true,
  },
  {
    route: '/invitations/:invitationId',
    docPath: '/user-management',
    anchor: 'view-invitation-details',
    label: 'Invitation Details',
    adminOnly: true,
  },
  {
    route: '/sessions',
    docPath: '/user-management',
    anchor: 'sessions',
    label: 'Sessions',
    adminOnly: true,
  },
  {
    route: '/sessions/:sessionId',
    docPath: '/user-management',
    anchor: 'view-session-details',
    label: 'Session Details',
    adminOnly: true,
  },
  {
    route: '/login-attempts',
    docPath: '/user-management',
    anchor: 'login-attempts',
    label: 'Login Attempts',
    adminOnly: true,
  },
  {
    route: '/login-attempts/:attemptId',
    docPath: '/user-management',
    anchor: 'review-a-login-attempt',
    label: 'Login Attempt Details',
    adminOnly: true,
  },

  // ─────────────────────────────────────────────────────────────────
  // Settings
  // ─────────────────────────────────────────────────────────────────
  {
    route: '/settings',
    docPath: '/getting-started',
    anchor: 'settings',
    label: 'Settings',
  },

];

export const DOCS_CONFIG: DocsLinkConfig = {
  baseUrl: 'https://access-control-docs.digiwedge.com',
  envUrlKey: 'VITE_DOCS_BASE_URL',
  windowUrlKey: '__ACCESS_CONTROL_DOCS_URL__',
  helpLinks: HELP_LINKS,
};
